import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft } from 'lucide-react';

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      <Helmet>
        <title>Cookie Policy — Practicio</title>
        <meta name="description" content="Practicio's Cookie Policy. Understand how we use cookies and similar technologies on our practice management platform." />
        <link rel="canonical" href="https://practicio.io/cookie-policy" />
      </Helmet>
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors mb-10" data-testid="cookie-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-slate-700 mb-2" data-testid="cookie-heading">Cookie Policy</h1>
          <p className="text-sm text-stone-400 mb-10">Last updated: February 2026</p>

          <div className="space-y-8 text-stone-600 leading-relaxed">
            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">1. What Are Cookies</h2>
              <p>
                Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work efficiently and to provide information to the site operators. Practicio uses cookies and similar technologies to enhance your experience on our platform.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">2. How We Use Cookies</h2>
              <p>We use the following types of cookies:</p>

              <div className="space-y-4 mt-2">
                <div className="bg-white border border-stone-200 rounded-lg p-4">
                  <h3 className="font-medium text-stone-900 mb-1">Essential Cookies</h3>
                  <p className="text-sm">Required for the platform to function. These include authentication cookies, session management, and security tokens. You cannot opt out of these cookies.</p>
                </div>
                <div className="bg-white border border-stone-200 rounded-lg p-4">
                  <h3 className="font-medium text-stone-900 mb-1">Functional Cookies</h3>
                  <p className="text-sm">Help us remember your preferences, such as language settings and display configurations. These improve your experience but are not strictly necessary.</p>
                </div>
                <div className="bg-white border border-stone-200 rounded-lg p-4">
                  <h3 className="font-medium text-stone-900 mb-1">Analytics Cookies</h3>
                  <p className="text-sm">Help us understand how visitors interact with our platform. We use this information to improve our services, identify popular features, and fix issues. Analytics data is aggregated and anonymised.</p>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">3. Third-Party Cookies</h2>
              <p>
                Some cookies on our platform are set by third-party services we use, such as analytics providers. These third parties have their own privacy policies governing the use of their cookies. We do not use advertising or tracking cookies from ad networks.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">4. Managing Cookies</h2>
              <p>
                You can control and manage cookies through your browser settings. Most browsers allow you to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>View cookies stored on your device</li>
                <li>Delete individual or all cookies</li>
                <li>Block cookies from specific or all websites</li>
                <li>Set preferences for first-party and third-party cookies</li>
              </ul>
              <p>
                Please note that blocking essential cookies may affect the functionality of the platform and prevent you from using certain features.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">5. Cookie Duration</h2>
              <p>
                <strong>Session Cookies:</strong> These are temporary and are deleted when you close your browser. They are used to maintain your session while you navigate the platform.
              </p>
              <p>
                <strong>Persistent Cookies:</strong> These remain on your device for a set period (typically up to 12 months) or until you delete them. They are used to remember your preferences and login state.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">6. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our business practices. We will post the updated policy on this page with a revised "last updated" date.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-base md:text-lg font-semibold text-stone-900">7. Contact Us</h2>
              <p>
                If you have questions about our use of cookies, contact us at{' '}
                <a href="mailto:hello@practicio.io" className="text-slate-600 hover:text-slate-700 font-medium underline underline-offset-2">
                  hello@practicio.io
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
